'use client';

import { useRouter, useSearchParams } from 'next/navigation';
import { useEffect, useState } from 'react';

import { Session } from '@/lib/getSessionPages';
import RegistrationModal from '@/modules/league/details/components/RegistrationModal';
import { LeagueDetails, SessionLeagueDetails } from '@/modules/league/new-components/types';

interface RegistrationModalsProps {
  params: { slug: string; sessionId: string };
  user?: Session;
  details: LeagueDetails;
  session: SessionLeagueDetails;
}

export const RegistrationModals = ({ params, user, details, session }: RegistrationModalsProps) => {
  const router = useRouter();
  const searchParams = useSearchParams();
  const { slug, sessionId } = params;

  const [modals, setModals] = useState({
    success: false,
    full: false,
    error: false,
    notOpened: false,
  });
  const [errorMessage, setErrorMessage] = useState('');

  const closeModal = (type: keyof typeof modals) => {
    setModals((prev) => ({ ...prev, [type]: false }));
  };

  const cleanSearchParams = (keysToRemove: string[]) => {
    if (searchParams) {
      const newParams = new URLSearchParams(searchParams);
      keysToRemove.forEach((key) => newParams.delete(key));
      router.replace(`?${newParams.toString()}`, { scroll: false });
    }
  };

  useEffect(() => {
    if (!user?.token || !searchParams) return;

    const svsu = searchParams.get('svsu');
    const err = searchParams.get('err');
    const msg = searchParams.get('msg');
    const full = searchParams.get('full');
    const rno = searchParams.get('rno');

    if (svsu) {
      setModals((prev) => ({ ...prev, success: true }));
      cleanSearchParams(['svsu']);
    } else if (err) {
      if (msg) {
        const sanitizedMessage = decodeURIComponent(msg).replace(/<\/?[^>]+(>|$)/g, '');
        setErrorMessage(sanitizedMessage);
        cleanSearchParams(['err', 'msg']);
      } else {
        cleanSearchParams(['err']);
      }
      setModals((prev) => ({ ...prev, error: true }));
    } else if (full) {
      setModals((prev) => ({ ...prev, full: true }));
      cleanSearchParams(['full']);
    } else if (rno) {
      setModals((prev) => ({ ...prev, notOpened: true }));
      cleanSearchParams(['rno']);
    }
  }, [searchParams, user?.token, router]);

  return (
    <>
      {/* Success Modal */}
      <RegistrationModal
        open={modals.success}
        showSuccessIcon
        iconColor='success-500'
        onClose={() => closeModal('success')}
        onConfirm={() => {
          closeModal('success');
          if (session?.MatchDateID_Cur) {
            const newUrl = new URL(
              `${process.env.NEXT_PUBLIC_PL_URL}/leagues/${slug}/session/${sessionId}/matchdate/${session.MatchDateID_Cur}?tab=rsvp`
            );
            router.push(newUrl.toString());
          }
        }}
        title={details?.Title}
        message={`You're successfully registered for this league. ${session?.MatchDateID_Cur ? 'Do you want to go to the game day?' : ''}`}
        {...(session?.MatchDateID_Cur
          ? {
              confirmButtonText: 'Yes',
              showCloseButton: true,
              closeButtonText: 'No',
            }
          : {})}
      />

      {/* Full League Modal */}
      <RegistrationModal
        open={modals.full}
        iconColor='yellow'
        title={details?.Title}
        message='The League is FULL.'
        onClose={() => closeModal('full')}
        onConfirm={() => closeModal('full')}
      />

      {/* Registration Not Open Modal */}
      <RegistrationModal
        open={modals.notOpened}
        iconColor='yellow'
        title={details?.Title}
        message='This registration is not open.'
        onClose={() => closeModal('notOpened')}
        onConfirm={() => closeModal('notOpened')}
      />

      {/* Error Modal */}
      <RegistrationModal
        open={modals.error}
        title={details?.Title}
        iconColor='error-500'
        message={
          <div className='flex flex-col gap-1'>
            <div>Signup Request Incomplete!</div>
            {errorMessage ? <div className='font-bold'>{errorMessage}</div> : null}
          </div>
        }
        confirmButtonText='Not a club member? Click to join.'
        onClose={() => closeModal('error')}
        onConfirm={() => {
          router.push(
            `${process.env.NEXT_PUBLIC_PC_URL}/clubs/${details?.ClubID}/membership-plans`
          );
        }}
      />
    </>
  );
};
